import React from "react";
import { createPortal } from "react-dom";
import styles from "./ota-firmware.module.scss";
import OtaDownloadModal from "../modals/ota-download-modal/ota-download-modal";
import OTAUpdateCard from "../../molecules/ota-update-card/ota-update-card";
import OTAFirmwareHistory from "../ota-firmware-history/ota-firmware-history";
import OtaFirmwareUploadModal from "../modals/ota-firmware-upload-modal/ota-firmware-upload-modal";
import OtaLoader from "@src/components/molecules/ota-loader/ota-loader";
import { OtaFirmwareVersionDrawerModal } from "../modals/ota-firmware-version-drawer-modal/ota-firmware-version-drawer-modal";
import { OtaFirmwareBackupDrawerModal } from "../modals/ota-firmware-backup-drawer-modal/ota-firmware-backup-drawer-modal";
import { useOtaFirmware } from "./use-ota-firmware";
import OTARequestForApproval from "@src/components/molecules/ota-request-for-approval/ota-request-for-approval";
import OTABackupProcessReviewRequestAndApprove from "@src/components/molecules/ota-backup-process-review-request-and-approve/ota-backup-process-review-request-and-approve";
import OTAPendingApprovalByOperator from "@src/components/molecules/ota-pending-approval-by-operator/ota-pending-approval-by-operator";
import OTABackupProcessPendingApprovalByOperator from "@src/components/molecules/ota-backup-process-pending-approval-by-operator/ota-backup-process-pending-approval-by-operator";
import OTAAcceptAndPush from "@src/components/molecules/ota-accept-and-push/ota-accept-and-push";
import OTABackupProcessAcceptAndCreate from "@src/components/molecules/ota-backup-process-accept-and-create/ota-backup-process-accept-and-create";
import OTARequestApprovalForAdmin from "@src/components/molecules/ota-request-approval-for-admin/ota-request-approval-for-admin";
import OTABackupProcessRequestApprovalForAdmin from "@src/components/molecules/ota-backup-process-request-approval-for-admin/ota-backup-process-request-approval-for-admin";
import OTAConfirmFirmwareInstallationModal from "../modals/ota-confirm-firmware-installation-modal/ota-confirm-firmware-installation-modal";
import OTAConfirmBackupProcessModal from "../modals/ota-confirm-backup-process-modal/ota-confirm-backup-process-modal";
import OTAAcceptAndPushFirmwareModal from "../modals/ota-accept-and-push-firmware-modal/ota-accept-and-push-firmware-modal";
import OTAAcceptAndCreateBackupProcessModal from "../modals/ota-accept-and-create-backup-process-modal/ota-accept-and-create-backup-process-modal";
import OTAInstallationInProgressModal from "../modals/ota-installation-in-progress-modal/ota-installation-in-progress-modal";
import OTAInstallationCompleteModal from "../modals/ota-installation-complete-modal/ota-installation-complete-modal";
import {
  INSTALL_AND_ACTION_BLOCK,
  ProcessTypes,
} from "@src/cloud-connect/ota-update/constants";
import OTAFirmwareInstallationInProgress from "@src/components/molecules/ota-firmware-installation-in-progress/ota-firmware-installation-in-progress";
import OTABackupProcessInProgress from "@src/components/molecules/ota-backup-process-in-progress/ota-backup-process-in-progress";
import OTAPushFirmwareModal from "../modals/ota-push-firmware-modal/ota-push-firmware-modal";
import OtaErrorBox from "@src/components/molecules/ota-error-box/ota-error-box";
import OTAInstallationFailedModal from "../modals/ota-installation-failed-modal/ota-installation-failed-modal";
import OTABackupProcessInProgressModal from "../modals/ota-backup-process-in-progress-modal/ota-backup-process-in-progress-modal";
import OtaCreateBackupModal from "../modals/ota-create-backup-modal/ota-create-backup-modal";

const cssClassName = "ota-firmware";

interface OTAFirmwareProps {
  fetchCurrentFirmwareVersion: () => Promise<void>;
}

const OTAFirmware = ({ fetchCurrentFirmwareVersion }: OTAFirmwareProps) => {
  const {
    modalRoot,
    versionInputValues,
    setVersionInputValues,
    backupInputValues,
    setBackupInputValues,
    activeOTAHistoryTab,
    setActiveOTAHistoryTab,
    firmwares,
    backups,
    activeList,
    isLoading,
    showOtaFirmwareVersionDrawerModal,
    showFirmwareUploadModal,
    showOtaFirmwareBackupDrawerModal,
    showOtaDownloadModal,
    fetchFirmwares,
    fetchBackups,
    closeFirmwareUploadModal,
    handleCloseOtaDownloadModal,
    setIsFirmwareLoading,
    setIsBackupLoading,
    showOTAPushFirmwareModal,
    showOTACreateBackupModal,
    showOTAPushFirmwareBackupModal,
    showOTAInstallationInProgressModal,
    showOTAInstallationCompleteModal,
    showOTAInstallationFailedModal,
    showOTAConfirmFirmwareInstallationModal,
    showOTAConfirmBackupProcessModal,
    showOTAAcceptAndPushFirmwareModal,
    showOTAAcceptAndCreateBackupProcessModal,
    activeInstallationData,
    installAndActionBlock,
    setInstallAndActionBlock,
    setSelectedPushFirmwareModalRow,
    setSelectedPushFirmwareBackupModalRow,
    pushFirmwareModalData,
    pushFirmwareBackupModalData,
    handleClosePushFirmwareModal,
    handleClosePushFirmwareBackupModal,
    handleCloseCreateBackupModal,
    handleCloseInstallationInProgressModal,
    handleCloseInstallationCompleteModal,
    showingAnyBlock,
    handleCloseOtaErrorBox,
    handleCloseInstallationFailedModal,
  } = useOtaFirmware(fetchCurrentFirmwareVersion);
  return (
    <div className={styles[cssClassName]}>
      {isLoading ? (
        <OtaLoader />
      ) : (
        <>
          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.INSTALLATION_FAILED &&
            activeInstallationData?.data != null && (
              <OtaErrorBox
                activeInstallationData={activeInstallationData?.data ?? null}
                onClose={handleCloseOtaErrorBox}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.REQUEST_FOR_APPROVAL &&
            activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
              <OTABackupProcessReviewRequestAndApprove
                activeInstallationData={activeInstallationData?.data ?? null}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.REQUEST_FOR_APPROVAL &&
            activeInstallationData?.data?.type !== ProcessTypes.BACKUP && (
              <OTARequestForApproval
                activeInstallationData={activeInstallationData?.data ?? null}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.PENDING_APPROVAL_BY_OPERATOR &&
            activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
              <OTABackupProcessPendingApprovalByOperator
                activeInstallationData={activeInstallationData?.data ?? null}
                setInstallAndActionBlock={setInstallAndActionBlock}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.PENDING_APPROVAL_BY_OPERATOR &&
            activeInstallationData?.data?.type !== ProcessTypes.BACKUP && (
              <OTAPendingApprovalByOperator
                activeInstallationData={activeInstallationData?.data ?? null}
                setInstallAndActionBlock={setInstallAndActionBlock}
              />
            )}

          {installAndActionBlock === INSTALL_AND_ACTION_BLOCK.ACCEPT_AND_PUSH &&
            activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
              <OTABackupProcessAcceptAndCreate
                activeInstallationData={activeInstallationData?.data ?? null}
              />
            )}

          {installAndActionBlock === INSTALL_AND_ACTION_BLOCK.ACCEPT_AND_PUSH &&
            activeInstallationData?.data?.type !== ProcessTypes.BACKUP && (
              <OTAAcceptAndPush
                activeInstallationData={activeInstallationData?.data}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.REQUEST_APPROVAL_FOR_ADMIN &&
            activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
              <OTABackupProcessRequestApprovalForAdmin
                activeInstallationData={activeInstallationData?.data ?? null}
                setInstallAndActionBlock={setInstallAndActionBlock}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.REQUEST_APPROVAL_FOR_ADMIN &&
            activeInstallationData?.data?.type !== ProcessTypes.BACKUP && (
              <OTARequestApprovalForAdmin
                activeInstallationData={activeInstallationData?.data ?? null}
                setInstallAndActionBlock={setInstallAndActionBlock}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.INSTALLATION_IN_PROGRESS &&
            activeInstallationData?.data?.type !== ProcessTypes.BACKUP && (
              <OTAFirmwareInstallationInProgress
                activeInstallationData={activeInstallationData?.data ?? null}
              />
            )}

          {installAndActionBlock ===
            INSTALL_AND_ACTION_BLOCK.INSTALLATION_IN_PROGRESS &&
            activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
              <OTABackupProcessInProgress
                activeInstallationData={activeInstallationData?.data ?? null}
              />
            )}

          <OTAUpdateCard
            cardData={activeList[0]}
            activeOTAHistoryTab={activeOTAHistoryTab}
            onSelectPushFirmwareRow={setSelectedPushFirmwareModalRow}
            onSelectPushFirmwareBackupRow={
              setSelectedPushFirmwareBackupModalRow
            }
            activeInstallationData={activeInstallationData?.data ?? null}
          />
          <OTAFirmwareHistory
            activeOTAHistoryTab={activeOTAHistoryTab}
            setActiveOTAHistoryTab={setActiveOTAHistoryTab}
            firmwares={firmwares}
            backups={backups}
            fetchMainList={fetchFirmwares}
            fetchBackups={fetchBackups}
            setIsFirmwareLoading={setIsFirmwareLoading}
            setIsBackupLoading={setIsBackupLoading}
            onSelectPushFirmwareRow={setSelectedPushFirmwareModalRow}
            onSelectPushFirmwareBackupRow={
              setSelectedPushFirmwareBackupModalRow
            }
            showingAnyBlock={showingAnyBlock}
            activeInstallationData={activeInstallationData?.data ?? null}
          />
        </>
      )}

      {modalRoot &&
        showOtaFirmwareVersionDrawerModal &&
        createPortal(
          <OtaFirmwareVersionDrawerModal
            inputValues={versionInputValues}
            setInputValues={setVersionInputValues}
          />,
          modalRoot,
        )}

      {showFirmwareUploadModal && (
        <OtaFirmwareUploadModal
          inputValues={versionInputValues}
          onClose={closeFirmwareUploadModal}
        />
      )}

      {modalRoot &&
        showOtaFirmwareBackupDrawerModal &&
        createPortal(
          <OtaFirmwareBackupDrawerModal
            inputValues={backupInputValues}
            setInputValues={setBackupInputValues}
            fetchBackups={fetchBackups}
          />,
          modalRoot,
        )}

      {showOtaDownloadModal && (
        <OtaDownloadModal onClose={handleCloseOtaDownloadModal} />
      )}

      {showOTAPushFirmwareModal && pushFirmwareModalData && (
        <OTAPushFirmwareModal
          data={pushFirmwareModalData}
          onClose={handleClosePushFirmwareModal}
          setInstallAndActionBlock={setInstallAndActionBlock}
        />
      )}

      {showOTACreateBackupModal && (
        <OtaCreateBackupModal onClose={handleCloseCreateBackupModal} />
      )}

      {showOTAPushFirmwareBackupModal && pushFirmwareBackupModalData && (
        <OTAPushFirmwareModal
          data={pushFirmwareBackupModalData}
          onClose={handleClosePushFirmwareBackupModal}
          setInstallAndActionBlock={setInstallAndActionBlock}
        />
      )}

      {showOTAConfirmFirmwareInstallationModal &&
        activeInstallationData?.data && (
          <OTAConfirmFirmwareInstallationModal
            data={activeInstallationData?.data}
            onClose={() => ({})}
            setInstallAndActionBlock={setInstallAndActionBlock}
          />
        )}

      {showOTAConfirmBackupProcessModal && activeInstallationData?.data && (
        <OTAConfirmBackupProcessModal
          data={activeInstallationData?.data}
          onClose={() => ({})}
        />
      )}

      {showOTAAcceptAndPushFirmwareModal && activeInstallationData?.data && (
        <OTAAcceptAndPushFirmwareModal
          data={activeInstallationData?.data}
          setInstallAndActionBlock={setInstallAndActionBlock}
        />
      )}

      {showOTAAcceptAndCreateBackupProcessModal &&
        activeInstallationData?.data && (
          <OTAAcceptAndCreateBackupProcessModal
            data={activeInstallationData?.data}
            setInstallAndActionBlock={setInstallAndActionBlock}
          />
        )}

      {showOTAInstallationInProgressModal &&
        activeInstallationData?.data &&
        activeInstallationData?.data?.type != ProcessTypes.BACKUP && (
          <OTAInstallationInProgressModal
            activeInstallationData={activeInstallationData?.data}
            onClose={handleCloseInstallationInProgressModal}
          />
        )}

      {showOTAInstallationInProgressModal &&
        activeInstallationData?.data &&
        activeInstallationData?.data?.type === ProcessTypes.BACKUP && (
          <OTABackupProcessInProgressModal
            activeInstallationData={activeInstallationData?.data}
            onClose={handleCloseInstallationInProgressModal}
          />
        )}

      {showOTAInstallationCompleteModal &&
        activeInstallationData?.data &&
        activeInstallationData?.data != null && (
          <OTAInstallationCompleteModal
            activeInstallationData={activeInstallationData?.data}
            onClose={handleCloseInstallationCompleteModal}
          />
        )}

      {showOTAInstallationFailedModal &&
        activeInstallationData?.data &&
        activeInstallationData?.data != null && (
          <OTAInstallationFailedModal
            activeInstallationData={activeInstallationData?.data}
            onClose={handleCloseInstallationFailedModal}
          />
        )}
    </div>
  );
};

export default OTAFirmware;
